/**
 * Admin Configuration Payload Builders
 * Constructs API request payloads and data transformations
 */

/**
 * Build admin config payload for API submission
 * @param {Object} config - Admin configuration
 * @returns {Object} API payload
 */
export const buildAdminConfigPayload = (config) => ({
  id: config.id,
  isActive: config.isActive,
  isHighPriority: config.isHighPriority,
  maxDailyLimit: config.maxDailyLimit,
});

/**
 * Build multiple admin configs payload
 * @param {Array<Object>} configs - Admin configurations
 * @returns {Array<Object>} API payloads
 */
export const buildAdminConfigsPayload = (configs) => configs.map(buildAdminConfigPayload);

/**
 * Disable high priority for all configs
 * Used when Round Robin mode is enabled
 * @param {Array<Object>} configs - Configuration array
 * @returns {Array<Object>} Updated configurations
 */
export const disableAllHighPriority = (configs) =>
  configs.map((config) => ({
    ...config,
    isHighPriority: false,
  }));

/**
 * Save priority states before Round Robin is enabled
 * @param {Array<Object>} configs - Configuration array
 * @returns {Object} Map of config id to high priority status
 */
export const savePriorityAdmins = (configs) => {
  return configs.reduce((acc, config) => {
    acc[config.id] = config.isHighPriority;
    return acc;
  }, {});
};

/**
 * Restore high priority admins from saved state
 * @param {Array<Object>} configs - Configuration array
 * @param {Object} priorityMap - Saved priority states
 * @returns {Array<Object>} Updated configurations
 */
export const restorePriorityAdmins = (configs, priorityMap) => {
  if (!priorityMap) return configs;
  
  return configs.map((config) => ({
    ...config,
    isHighPriority: priorityMap[config.id] || false,
  }));
};

/**
 * Build Round Robin configuration payload
 * @param {boolean} isEnabled - Round Robin enabled status
 * @returns {Object} API payload
 */
export const buildRoundRobinPayload = (isEnabled) => ({
  roundRobinEnabled: isEnabled,
});

/**
 * Build configurations for save state
 * @param {Array<Object>} adminConfigs - Admin configurations
 * @param {boolean} isRoundRobinEnabled - Round Robin status
 * @param {Function} deepClone - Clone utility
 * @returns {Object} Saved configuration state
 */
export const buildSavedConfigurations = (adminConfigs, isRoundRobinEnabled, deepClone) => ({
  isRoundRobinEnabled,
  adminConfigs: deepClone ? deepClone(adminConfigs) : [...adminConfigs],
});
